import { useEffect, useRef } from "react";
import axios from "axios";
import { useEliteStructure } from "../contexts/EliteTemporaryContextProvider";
import { environment } from "../config/env";

export interface EliteThemeConfig {
  light: Record<string, string>;
  dark: Record<string, string>;
}

let _cachedTheme: EliteThemeConfig | null = null;
export function getCachedTheme(): EliteThemeConfig | null {
  return _cachedTheme;
}

const PROP_MAP: Record<string, string> = {
  primary: "--primary",
  primaryForeground: "--primary-foreground",
  background: "--background",
  foreground: "--foreground",
  card: "--card",
  cardForeground: "--card-foreground",
  popover: "--popover",
  popoverForeground: "--popover-foreground",
  secondary: "--secondary",
  secondaryForeground: "--secondary-foreground",
  muted: "--muted",
  mutedForeground: "--muted-foreground",
  accent: "--accent",
  accentForeground: "--accent-foreground",
  destructive: "--destructive",
  border: "--border",
  input: "--input",
  ring: "--ring",
  chart1: "--chart-1",
  chart2: "--chart-2",
  chart3: "--chart-3",
  chart4: "--chart-4",
  chart5: "--chart-5",
  sidebar: "--sidebar",
  sidebarForeground: "--sidebar-foreground",
  sidebarPrimary: "--sidebar-primary",
  sidebarPrimaryForeground: "--sidebar-primary-foreground",
  sidebarAccent: "--sidebar-accent",
  sidebarAccentForeground: "--sidebar-accent-foreground",
  sidebarBorder: "--sidebar-border",
  sidebarRing: "--sidebar-ring",
  borderRadius: "--radius",
  fontFamily: "--font-sans",
  headingFontFamily: "--font-serif",
};

export function applyTheme(
  config: EliteThemeConfig,
  mode: "light" | "dark" = "light",
) {
  const root = document.documentElement;
  const vars = config[mode];
  if (!vars) return;

  Object.entries(PROP_MAP).forEach(([key, cssVar]) => {
    const val = vars[key];
    if (val) {
      root.style.setProperty(cssVar, val);
    }
  });
}

export function useEliteTheme() {
  const { structure } = useEliteStructure();
  const appliedRef = useRef<number | null>(null);

  useEffect(() => {
    const structureId = structure?.id;
    if (!structureId) return;

    if (appliedRef.current === structureId) return;

    const controller = new AbortController();

    axios
      .get(`${environment.ELITE_BASE_URI}/EliteAppTheme/${structureId}`, {
        signal: controller.signal,
      })
      .then(({ data }) => {
        if (!data?.theme) return;
        const parsed: EliteThemeConfig = JSON.parse(data.theme);
        _cachedTheme = parsed;
        appliedRef.current = structureId;

        const isDark = document.documentElement.classList.contains("dark");
        applyTheme(parsed, isDark ? "dark" : "light");
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        if (err?.response?.status === 404) {
          _cachedTheme = null;
          return;
        }
        console.warn("[useEliteTheme]", err);
      });

    return () => controller.abort();
  }, [structure?.id]);
}
